import React, { useState } from 'react';
import { verifyIFSC, isValidIFSCFormat, formatIFSC } from '../lib/ifscVerification';

const IFSCVerification = ({ onVerified, initialValue = '', className = '' }) => {
    const [ifscCode, setIfscCode] = useState(initialValue);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [bankDetails, setBankDetails] = useState(null);
    const [error, setError] = useState('');

    const handleIFSCChange = (e) => {
        const value = formatIFSC(e.target.value);
        setIfscCode(value);
        setIsVerified(false);
        setBankDetails(null);
        setError('');
    };

    const handleVerify = async () => {
        if (!ifscCode) {
            setError('Please enter IFSC code');
            return;
        }

        if (!isValidIFSCFormat(ifscCode)) {
            setError('Invalid IFSC format. Must be 11 characters (e.g., SBIN0001234)');
            return;
        }

        setIsVerifying(true);
        setError('');

        try {
            const details = await verifyIFSC(ifscCode);
            setBankDetails(details);
            setIsVerified(true);
            setError('');

            // Notify parent component
            if (onVerified) {
                onVerified({
                    ifscCode: details.ifsc,
                    bankName: details.bank,
                    branchName: details.branch,
                    address: details.address,
                    city: details.city,
                    state: details.state
                });
            }
        } catch (err) {
            setError(err.message);
            setIsVerified(false);
            setBankDetails(null);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleVerify();
        }
    };

    return (
        <div className={`ifsc-verification ${className}`}>
            {/* IFSC Input */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-2">
                    <input
                        type="text"
                        value={ifscCode}
                        onChange={handleIFSCChange}
                        onKeyPress={handleKeyPress}
                        maxLength={11}
                        placeholder="e.g., SBIN0001234"
                        className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase ${error ? 'border-red-500' : isVerified ? 'border-green-500' : 'border-gray-300'
                            }`}
                        disabled={isVerifying}
                    />
                    <button
                        type="button"
                        onClick={handleVerify}
                        disabled={isVerifying || !ifscCode || ifscCode.length !== 11}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center space-x-2"
                    >
                        {isVerifying ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                <span>Verifying...</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>Verify</span>
                            </>
                        )}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    Enter 11-character IFSC code (e.g., SBIN0001234)
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <svg className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-red-700">{error}</span>
                </div>
            )}

            {/* Success - Bank Details */}
            {isVerified && bankDetails && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start mb-3">
                        <svg className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                            <h4 className="text-sm font-semibold text-green-900 mb-2">✓ IFSC Verified Successfully</h4>
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <p className="text-xs text-gray-600">Bank Name</p>
                                        <p className="text-sm font-medium text-gray-900">{bankDetails.bank}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-600">Branch</p>
                                        <p className="text-sm font-medium text-gray-900">{bankDetails.branch}</p>
                                    </div>
                                </div>
                                {bankDetails.address && (
                                    <div>
                                        <p className="text-xs text-gray-600">Address</p>
                                        <p className="text-sm text-gray-900">{bankDetails.address}</p>
                                    </div>
                                )}
                                <div className="grid grid-cols-3 gap-2">
                                    {bankDetails.city && (
                                        <div>
                                            <p className="text-xs text-gray-600">City</p>
                                            <p className="text-sm text-gray-900">{bankDetails.city}</p>
                                        </div>
                                    )}
                                    {bankDetails.state && (
                                        <div>
                                            <p className="text-xs text-gray-600">State</p>
                                            <p className="text-sm text-gray-900">{bankDetails.state}</p>
                                        </div>
                                    )}
                                    {bankDetails.micr && (
                                        <div>
                                            <p className="text-xs text-gray-600">MICR</p>
                                            <p className="text-sm text-gray-900">{bankDetails.micr}</p>
                                        </div>
                                    )}
                                </div>
                                {/* Payment Methods */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t border-green-200">
                                    <p className="text-xs text-gray-600 w-full">Supported Payment Methods:</p>
                                    {bankDetails.rtgs && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            RTGS
                                        </span>
                                    )}
                                    {bankDetails.neft && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            NEFT
                                        </span>
                                    )}
                                    {bankDetails.imps && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            IMPS
                                        </span>
                                    )}
                                    {bankDetails.upi && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            UPI
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IFSCVerification;
